import { ChangeEvent, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import {
  AgreementFileReadModel,
  PermissionKeys,
  useGetAgreementsByIdQuery,
  usePostAgreementsByAgreementIdFilesMutation,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
} from '@client/toolkit';

import { AgreementFileDeleteModal } from './agreement-file-delete-modal.component';
import { AgreementFileItem } from './agreement-file-item.component';
import { useAgreementPermissions } from '../../../../../hooks/useAgreementPermissions/useAgreementPermissions';

export interface AgreementFilesCardProps {
  className?: string;
  title?: string;
}

export function AgreementFilesCard({ className, ...props }: AgreementFilesCardProps) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const { title = tGlobal('entity.agreementFile', { context: 'plural' }) } = props;

  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAgreementPermissions({ id: id ?? '' });

  const pageSize = 20;
  const [page, setPage] = useState(0);

  const { data: agreement, isFetching, isLoading } = useGetAgreementsByIdQuery({ id: id ?? '' }, { skip: id == null });

  const agreementFiles = useMemo(() => agreement?.files ?? [], [agreement?.files]);
  const totalCount = agreementFiles != null && agreementFiles.length > 0 ? agreementFiles.length : 0;

  const hasAgreementFiles = agreementFiles != null && agreementFiles.length <= 0;
  const isListEmpty = useMemo(() => hasAgreementFiles, [hasAgreementFiles]);

  /* Delete */

  const [deletingItem, setDeletingItem] = useState<boolean>(false);
  const [deletingItemModel, setDeletingItemModel] = useState<AgreementFileReadModel>();

  const handleOpenDeleteItem = (model: AgreementFileReadModel) => {
    setDeletingItem(true);
    setDeletingItemModel(model);
  };

  const handleCloseDeleteItem = () => {
    setDeletingItem(false);
  };

  const handleDeleteItemFulfilled = () => {
    setDeletingItem(false);
  };

  /* --------- */

  /* Upload */

  const [postFile, { isLoading: isUploading }] = usePostAgreementsByAgreementIdFilesMutation();

  const inputRef = useRef<HTMLInputElement>(null);
  const handleClickUpload = useCallback(() => inputRef.current?.click(), [inputRef]);
  const handleChangeInput = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (id == null) return;

      const fileContent = event.target.files?.[0];
      if (!fileContent) return;

      const body = new FormData();
      body.append('File', fileContent);

      await postFile({ agreementId: id, body: body as any });

      if (inputRef.current?.files) {
        inputRef.current.files = null;
      }
      if (inputRef.current?.value) {
        inputRef.current.value = '';
      }
    },
    [id, postFile],
  );

  /* ---------- */

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex-1" variant="highlighted">
            {title}
          </CardTitle>
          <CardActions>
            {hasPermission([PermissionKeys.Agreement.Update]) && (
              <Button
                size="sm"
                color="primary"
                onClick={handleClickUpload}
                disabled={isFetching}
                progressing={isUploading}
              >
                <input ref={inputRef} type="file" className="hidden" onChange={handleChangeInput} />

                {tGlobal('upload')}
              </Button>
            )}
          </CardActions>
        </CardHeader>

        <CardContent variant="fill" className="mx-4 mb-6 justify-self-center border border-border rounded-lg">
          <Table className="overflow-hidden w-full mb-4" size="xs" layout="fixed">
            {!isLoading && isListEmpty && (
              <caption className="px-4 py-4 [caption-side:bottom]">
                <EmptyPlaceholder>
                  {tCommon('noEntitySpecified', {
                    entity: tGlobal('entity.agreementFile', { context: 'plural' }).toLowerCase(),
                  })}
                </EmptyPlaceholder>
              </caption>
            )}

            <TableHead>
              <TableHeadRow>
                <TableHeadCell>{tGlobal('entity.file')}</TableHeadCell>
              </TableHeadRow>
            </TableHead>

            <TableBody>
              {isLoading && Array.from(Array(3).keys()).map((x, idx) => <AgreementFileItem key={x} isFetching />)}

              {!isLoading &&
                agreementFiles?.map((file) => (
                  <AgreementFileItem
                    key={file.id}
                    agreementFile={file}
                    isFetching={isFetching}
                    onClickDelete={handleOpenDeleteItem}
                  />
                ))}
            </TableBody>
          </Table>

          <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
        </CardContent>
      </Card>

      <AgreementFileDeleteModal
        open={deletingItem}
        agreementFile={deletingItemModel}
        onCancel={handleCloseDeleteItem}
        onDeleteFulfilled={handleDeleteItemFulfilled}
      />
    </>
  );
}
